import { useEffect } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { AppShell, goToSsoLogin, showToast, signOut, ThemeVars, Topbar, Trans, useWebTheme } from '@orgazone/ui-web';
import { BackofficeUiHost } from './ui-host';
import { ImportJobNotice } from './screens/import-job-notice';
import { PAGE_PAD } from '@orgazone/ui-tokens';
import { Rail } from './rail';
import { labelOf } from './nav';
import { StoreContextBanner, StoreScopeProvider } from './store-context';
import { Dashboard } from './screens/dashboard';
import { Placeholder } from './screens/placeholder';
import { StatsVentes } from './screens/stats-ventes';
import { StatsStock } from './screens/stats-stock';
import { StatsVendeurs } from './screens/stats-vendeurs';
import { StatsClients } from './screens/stats-clients';
import { Prix } from './screens/prix';
import { Receptions } from './screens/receptions';
import { Retrocessions } from './screens/retrocessions';
import { Ecarts } from './screens/ecarts';
import { Stock } from './screens/stock';
import { Mouvements } from './screens/mouvements';
import { ArticlesRapides } from './screens/articles-rapides';
import { Promotions } from './screens/promotions';
import { Parametres } from './screens/parametres';
import { Magasin } from './screens/magasin';
import { Activite } from './screens/activite';
import { Relances } from './screens/relances';
import { Operateurs } from './screens/operateurs';
import { Etiquettes } from './screens/etiquettes';
import { AValider } from './screens/a-valider';
import { Inventaires } from './screens/inventaires';
import { Referentiels } from './screens/referentiels';
import { Acces } from './screens/acces';
import { Articles } from './screens/articles';
import { Clients } from './screens/clients';
import { Marketing } from './screens/marketing';
import { GabaritsEmail } from './screens/gabarits-email';
import { Titres } from './screens/titres';
import { Fidelite } from './screens/fidelite';
import { Journal } from './screens/journal';
import { Objectifs } from './screens/objectifs';
import { Cloture } from './screens/cloture';
import { Integrations } from './screens/integrations';
import { Archives } from './screens/archives';
import { SanteNf525 } from './screens/sante-nf525';
import { CentreNotifications } from './notifications';
import { ExportCompta } from './screens/export-compta';
import { MonCompte } from './screens/mon-compte';
import { StaffStrip } from './staff-strip';
import { PaletteRecherche } from './palette';
import { Login } from './screens/login';
import { Reset } from './screens/reset';
import { api } from './api';
import { AuthProvider, useAuth } from './auth';
import { NAV, OFF_MENU, RESTRICTED } from './nav';
import { key, t, type Translatable } from './i18n';

/** Le rendu d'un chemin d'URL dans une phrase traduite — voir le `<Trans>` de la page inconnue. */
const CHEMIN_MONO = (chemin: string) => <code style={{ fontFamily: 'var(--font-mono)' }}>{chemin}</code>;

/**
 * LE BACK-OFFICE MAGASIN — coquille, navigation et porte d'entrée.
 *
 * ⚠️ UNE DESTINATION SANS ÉCRAN NE REND JAMAIS UNE MAQUETTE PEUPLÉE : `ecranDe` lui donne l'état qui dit
 * ce qui lui manque (`Placeholder`), jamais des données de démonstration (§23.0).
 */
export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        {/* Charte plateforme : défaut sombre comme les autres surfaces staff. ⚠️ Le rail s'INVERSE
            (sombre en thème clair, clair en sombre) — c'est le défaut de la convention, conservé pour le
            back-office là où la Console diverge en le gardant sombre en permanence (CADRAGE §41). */}
        <ThemeVars defaultMode="light" />
        <BackofficeUiHost />
        {/* ⚠️ DANS LE ROUTEUR, HORS DES ÉCRANS : l'import de fichier continue quand l'assistant se ferme, et sa
            notification doit survivre à un changement d'écran — « Voir les articles » y navigue. */}
        <ImportJobNotice />
        <Routes>
          {/* Hors coquille : on ne montre ni rail ni contexte magasin à qui n'est pas encore entré. */}
          <Route path="/login" element={<Login />} />
          <Route path="/reset" element={<Reset />} />
          <Route element={<Porte />}>
            {ROUTES.map((r) => (
              <Route key={r.path} path={r.path} element={ecranDe(r)} />
            ))}
            {/* ⚠️ Pas de redirection silencieuse vers l'accueil sur un chemin inconnu : elle ferait passer
                une URL fausse (lien périmé, faute de frappe) pour une navigation réussie. On le dit. */}
            <Route path="*" element={<UnknownRoute />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

/** L'écran d'une destination — construit, ou l'état qui dit ce qui lui manque. */
function ecranDe(r: { path: string; label: Translatable }) {
  if (r.path === '/') return <Dashboard />;
  if (r.path === '/acces') return <Acces />;
  if (r.path === '/articles') return <Articles />;
  if (r.path === '/clients') return <Clients />;
  if (r.path === '/titres') return <Titres />;
  if (r.path === '/marketing') return <Marketing />;
  if (r.path === '/gabarits-email') return <GabaritsEmail />;
  if (r.path === '/fidelite') return <Fidelite />;
  if (r.path === '/journal') return <Journal />;
  if (r.path === '/objectifs') return <Objectifs />;
  if (r.path === '/cloture') return <Cloture />;
  if (r.path === '/archives') return <Archives />;
  if (r.path === '/sante-nf525') return <SanteNf525 />;
  if (r.path === '/referentiels') return <Referentiels />;
  if (r.path === '/integrations') return <Integrations />;
  if (r.path === '/export-compta') return <ExportCompta />;
  if (r.path === '/receptions') return <Receptions />;
  if (r.path === '/retrocessions') return <Retrocessions />;
  if (r.path === '/ecarts') return <Ecarts />;
  if (r.path === '/stock') return <Stock />;
  if (r.path === '/mouvements') return <Mouvements />;
  if (r.path === '/articles-rapides') return <ArticlesRapides />;
  if (r.path === '/prix') return <Prix />;
  if (r.path === '/promotions') return <Promotions />;
  if (r.path === '/parametres') return <Parametres />;
  if (r.path === '/operateurs') return <Operateurs />;
  if (r.path === '/etiquettes') return <Etiquettes />;
  if (r.path === '/a-valider') return <AValider />;
  if (r.path === '/inventaires') return <Inventaires />;
  if (r.path === '/magasin') return <Magasin />;
  if (r.path === '/activite') return <Activite />;
  if (r.path === '/relances') return <Relances />;
  if (r.path === '/mon-compte') return <MonCompte />;
  if (r.path === '/stats/ventes') return <StatsVentes />;
  if (r.path === '/stats/stock') return <StatsStock />;
  if (r.path === '/stats/vendeurs') return <StatsVendeurs />;
  if (r.path === '/stats/clients') return <StatsClients />;
  return <Placeholder title={t(r.label)} path={r.path} />;
}

/** Toutes les destinations connues du rail, à plat — le routeur n'invente aucun chemin. */
const ROUTES = [
  ...NAV.flatMap((e) => (e.kind === 'leaf' ? [{ path: e.path, label: e.label }] : e.children.map((c) => ({ path: c.path, label: c.label })))),
  ...RESTRICTED.map((r) => ({ path: r.path, label: r.label })),
  ...OFF_MENU.map((r) => ({ path: r.path, label: r.label })),
];

/**
 * LA PORTE : aucun écran sans session.
 *
 * ⚠️ AUCUN « mode ouvert ». Le back-office administre des magasins clients : son API exige une session
 * sans exception, donc l'écran l'exige aussi. Laisser entrer sans session n'afficherait que des 401.
 * ⚠️ Tant que la session n'est pas RÉSOLUE, on ne rend rien : afficher la coquille puis la remplacer par
 * l'écran de connexion ferait clignoter une application à laquelle on n'a pas encore accès.
 */
function Porte() {
  const { ready, user, mode } = useAuth();
  if (!ready) return null;
  if (!user) return <SeConnecter oidc={mode.oidc} />;
  return <Coquille />;
}

/**
 * Envoie se connecter — en SSO quand l'IdP est configuré, sur l'écran local sinon.
 *
 * ⚠️ NAVIGATION PLEINE PAGE pour le SSO (`goToSsoLogin`, séquence partagée) : `/v1/auth/oidc/login` est
 * une route SERVEUR, elle pose le cookie d'état PKCE avant de partir chez l'IdP. Passer par le routeur
 * ne ferait rien. Le détour par l'écran local était le défaut de l'EDI : arriver avec une session
 * Orgazone déjà ouverte affichait un formulaire au lieu de connecter.
 */
function SeConnecter({ oidc }: { oidc: boolean }) {
  useEffect(() => { if (oidc) goToSsoLogin(); }, [oidc]);
  if (!oidc) return <Navigate to="/login" replace />;
  return null;
}

function Coquille() {
  const { user, mode } = useAuth();
  // ⚠️ MÉCANIQUE PARTAGÉE, pas un état local. Le thème vivait dans un `useState` de ce composant
  // pendant que <ThemeVars defaultMode="dark"/> restait figé : la bascule changeait l'ICÔNE et rien
  // d'autre — un bouton qui fait semblant (§23.0, interdit n°4). `useWebTheme` pose `data-theme` sur le
  // document ET mémorise le choix, comme dans les trois autres surfaces.
  // ⚠️ CLAIR PAR DÉFAUT (décision utilisateur 2026-08-17). Le défaut ne vaut QUE pour un poste sans
  // préférence enregistrée : un navigateur qui a déjà choisi « sombre » garde son choix — c'est le
  // sien, et l'écraser au nom d'un défaut serait pire que le défaut lui-même.
  const { mode: theme, toggle: basculer } = useWebTheme('oz-backoffice-theme', 'light');

  /**
   * DÉCONNEXION RÉELLE, en deux temps quand il y a un IdP : révoquer notre session, PUIS terminer celle
   * de l'IdP. ⚠️ S'arrêter au premier temps ne déconnecte pas : tant que la session SSO vit, la
   * navigation suivante ré-authentifie EN SILENCE et l'utilisateur se croit sorti d'un poste qui ne
   * l'est pas (constaté en production le 2026-08-11). La séquence vit dans `@orgazone/ui-web`.
   * ⚠️ Sans IdP (bris de glace seul), la route de fin de session de l'IdP n'existe pas : on révoque et
   * on retourne à l'écran local. Et un ÉCHEC ne navigue pas — afficher une déconnexion qui n'a pas eu
   * lieu est le plus grave des mensonges d'écran (§23.0).
   */
  const seDeconnecter = async () => {
    if (mode.oidc) {
      await signOut(() => api.logout(), { onError: (m) => showToast(t('Déconnexion impossible : {motif}', { motif: m }), 'err') });
      return;
    }
    try {
      await api.logout();
      window.location.assign('/login');
    } catch (e) {
      showToast(t('Déconnexion impossible : {motif}', { motif: e instanceof Error ? e.message : String(e) }), 'err');
    }
  };

  return (
    // ⚠️ LE CONTEXTE MAGASIN ENVELOPPE LE BANDEAU **ET** LES ÉCRANS : les deux lisent le même magasin
    // actif, donc l'écran ne peut pas afficher les articles d'un magasin sous le nom d'un autre.
    <StoreScopeProvider>
      <AppShell
        rail={<Rail user={{ name: user!.name, role: user!.roleLabel ? t(user!.roleLabel) : '', permissions: user!.permissions }} onLogout={seDeconnecter} />}
        header={<>
          {/* ⚠️ AU-DESSUS DE LA TOPBAR, pleine largeur : la bande noire dit qu'on agit chez un client.
              Dans la topbar, elle passait pour un élément d'interface de plus. */}
          <StaffStrip />
          {/* ⚠️ PAS de bascule dans `actions` : `Topbar` en pose déjà une depuis `mode`/`onToggleTheme`.
              En passer une seconde affichait deux boutons soleil identiques côte à côte. */}
          {/* ⚠️ 🔴 LA CLOCHE N'EST PLUS INERTE : elle lit un vrai centre (table, périmètre, état « lu »
              par personne). La règle qu'elle portait tient toujours, et elle a simplement changé de
              main — PAS DE PASTILLE INVENTÉE : le compte vient du serveur, ne compte que les
              notifications OUVERTES et NON LUES, et ne s'affiche pas à zéro (§23.0).
              ⚠️ CE QUI RESTE VRAI, ET QUE L'ÉCRAN DIT : aucun producteur ne l'alimente encore, donc un
              centre vide ne veut pas dire « rien à signaler ». La phrase vient du serveur, qui seul
              sait s'il a déjà reçu quelque chose — le jour où un producteur existe, elle disparaît
              d'elle-même. */}
          <Topbar
            mode={theme}
            onToggleTheme={basculer}
            actions={
              <>
                <PaletteRecherche />
                <CentreNotifications />
              </>
            }
          >
            <StoreContextBanner />
          </Topbar>
        </>}>

        {/* ⚠️ Respiration de la charte (`PAGE_PAD`) : les trois outils posaient trois valeurs
            différentes pour le même gabarit, donc trois alignements de titre. */}
        <main style={{ padding: `${PAGE_PAD.top}px ${PAGE_PAD.side}px ${PAGE_PAD.bottom}px`, minWidth: 0 }}>
          <Outlet />
        </main>
      </AppShell>
    </StoreScopeProvider>
  );
}

/** Chemin inconnu : on nomme l'adresse demandée, seule information utile pour comprendre l'erreur. */
function UnknownRoute() {
  const { pathname } = useLocation();
  return (
    <div className="dcard" style={{ padding: 24, maxWidth: 640 }}>
      <h1 style={{ fontSize: 'var(--t-h2)', margin: '0 0 8px' }}>{t('Cette adresse n’existe pas')}</h1>
      <p style={{ color: 'var(--ink-500)', fontSize: 'var(--t-body)', lineHeight: 1.6, margin: 0 }}>
        
        <Trans k="Aucun écran du back-office ne répond à <1>{chemin}</1>." vars={{ chemin: pathname }} parts={[CHEMIN_MONO]} />
        {/* ⚠️ LE LIBELLÉ DE NAVIGATION EST UNE CLÉ : passé tel quel en valeur, il restait français au milieu d'une
            phrase traduite. Il se résout avant d'entrer dans la phrase. */}
        {' '}{t('Le libellé attendu était peut-être {libelle}.', { libelle: t(labelOf(pathname) ?? key('inconnu de la navigation')) })}
      </p>
    </div>
  );
}
